import { useState, useRef } from 'react';
import axios from 'axios';
import { API, recentApiErrors } from '@/App';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { AlertCircle, Send, Camera, X } from 'lucide-react';

const MAX_PHOTOS = 5;

// Buton de raportare a problemelor — șoferul confirmă trimiterea cu un click.
// Detaliile tehnice (ultimele erori de API) se adună automat, dar se trimit
// doar când utilizatorul apasă „Trimite raport". Opțional poate atașa poze.
export const ReportProblemButton = ({ auth, section, label = 'Trimite raport', variant = 'outline', size = 'sm', className = '' }) => {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [photos, setPhotos] = useState([]); // { file, url }
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);

  const errors = (recentApiErrors || []).slice(0, 5);
  const headers = { Authorization: `Bearer ${auth?.token}` };

  const addPhotos = (e) => {
    const files = Array.from(e.target.files || []);
    const room = MAX_PHOTOS - photos.length;
    if (room <= 0) { toast.error(`Maxim ${MAX_PHOTOS} poze`); return; }
    const picked = files.slice(0, room).map(f => ({ file: f, url: URL.createObjectURL(f) }));
    setPhotos(prev => [...prev, ...picked]);
    if (fileRef.current) fileRef.current.value = '';
  };

  const removePhoto = (idx) => {
    setPhotos(prev => {
      const next = [...prev];
      try { URL.revokeObjectURL(next[idx].url); } catch { /* noop */ }
      next.splice(idx, 1);
      return next;
    });
  };

  const reset = () => {
    photos.forEach(p => { try { URL.revokeObjectURL(p.url); } catch { /* noop */ } });
    setPhotos([]);
    setNote('');
  };

  const submit = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/error-reports`, {
        message: note,
        app_section: section || null,
        url: window.location.href,
        user_agent: navigator.userAgent,
        recent_errors: errors,
      }, { headers });

      // Încarcă pozele (dacă există)
      let failed = 0;
      for (const p of photos) {
        const fd = new FormData();
        fd.append('file', p.file);
        try {
          await axios.post(`${API}/error-reports/${data.id}/photo`, fd, { headers });
        } catch { failed += 1; }
      }

      if (failed > 0) {
        toast.warning(`Raport trimis, dar ${failed} ${failed === 1 ? 'poză nu a putut fi încărcată' : 'poze nu au putut fi încărcate'}.`);
      } else {
        toast.success('Raportul a fost trimis către administrator. Mulțumim!');
      }
      setOpen(false);
      reset();
    } catch {
      toast.error('Nu s-a putut trimite raportul. Încearcă din nou.');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Button variant={variant} size={size} className={className} onClick={() => setOpen(true)} data-testid="report-problem-btn">
        <AlertCircle className="w-4 h-4 mr-1.5" />{label}
      </Button>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent className="max-w-md" data-testid="report-problem-dialog">
          <DialogHeader>
            <DialogTitle>Raportează o problemă</DialogTitle>
            <DialogDescription>
              Detaliile tehnice se trimit automat. Poți adăuga o descriere și poze cu ce vezi pe ecran.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <Textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Ex: am intrat la Foaie de Parcurs și nu apar mașinile..."
              className="h-24"
              data-testid="report-problem-note"
            />

            {/* Poze */}
            <div>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                className="hidden"
                onChange={addPhotos}
                data-testid="report-problem-file-input"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileRef.current?.click()}
                disabled={photos.length >= MAX_PHOTOS}
                data-testid="report-problem-add-photo"
              >
                <Camera className="w-4 h-4 mr-1.5" />
                Adaugă poză {photos.length > 0 ? `(${photos.length}/${MAX_PHOTOS})` : ''}
              </Button>

              {photos.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2" data-testid="report-problem-photos">
                  {photos.map((p, i) => (
                    <div key={i} className="relative w-16 h-16 rounded-md overflow-hidden border border-slate-200">
                      <img src={p.url} alt={`poza-${i}`} className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removePhoto(i)}
                        className="absolute top-0 right-0 bg-red-600 text-white rounded-bl-md p-0.5"
                        data-testid={`report-problem-remove-photo-${i}`}
                        aria-label="Șterge poza"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {errors.length > 0 && (
              <div className="rounded-md border border-slate-200 bg-slate-50 p-2 text-xs text-slate-600" data-testid="report-problem-tech">
                <p className="font-semibold mb-1 text-slate-500">Detalii tehnice (automat):</p>
                {errors.map((e, i) => (
                  <div key={i} className="font-mono truncate">
                    {e.status || '—'} · {(e.method || '').toUpperCase()} {e.endpoint || ''} {e.detail ? `· ${e.detail}` : ''}
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpen(false); reset(); }} data-testid="report-problem-cancel">Anulează</Button>
            <Button onClick={submit} disabled={sending} data-testid="report-problem-send">
              <Send className="w-4 h-4 mr-1.5" />{sending ? 'Se trimite...' : 'Trimite raport'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ReportProblemButton;
